import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { useIsAdmin } from "@/shared/hooks/use-is-admin";
import { ROUTES, SETTINGS_ROUTES } from "@/shared/constants/routes";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuestionnaireWizardStore } from "../../states/questionnaire-wizard";
import { useSettingsHeaderStore } from "../../states/settings-header";
import CreateQuestionnaireScreen from "../create-questionnaire-screen";

const SettingsLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isWizardOpen, questionnaireId, openWizard, closeWizard } = useQuestionnaireWizardStore();
  const { handleSave, isSaving, isDisabled } = useSettingsHeaderStore();
  const isAdmin = useIsAdmin();

  // If questionnaire wizard is open, render it as full-screen overlay
  if (isWizardOpen) {
    return (
      <div
        className="fixed inset-0 z-50 p-4"
        style={{
          background: `linear-gradient(
            180deg,
            #f4f4f5 0%,
            #f8e3ea 23.5%,
            #fdd0df 47.5%,
            #efd2ea 73%,
            #e4d4f4 100%
          )`
        }}
      >
        <CreateQuestionnaireScreen onClose={closeWizard} questionnaireId={questionnaireId || undefined} />
      </div>
    );
  }

  // Navigate back to settings with the requested tab active (query param)
  const navigateBackToThirdParty = () => {
    const returnTab = location.state?.returnTab || 'third-party';
    navigate(`/${ROUTES.SETTINGS}?tab=${returnTab}`, { replace: true });
  };

  const navigateBackToGeneral = () => {
    navigate(`/${ROUTES.SETTINGS}`, {
      state: { tab: "general" },
      replace: true,
    });
  };

  // Determine breadcrumb items based on current route
  const getBreadcrumbItems = () => {
    const path = location.pathname;

    if (path.includes(SETTINGS_ROUTES.THIRD_PARTY_QUESTIONNAIRES)) {
      return [
        { title: "Third-Party Risk Agent Settings", href: "", onClick: navigateBackToThirdParty },
        { title: "Questionnaire & Document Sets", href: "" }
      ];
    }

    if (path.includes(SETTINGS_ROUTES.THIRD_PARTY_CONTROL_SETS)) {
      return [
        { title: "Third-Party Risk Agent Settings", href: "", onClick: navigateBackToThirdParty },
        { title: "Control Sets", href: "" }
      ];
    }

    if (path.includes(SETTINGS_ROUTES.INTAKE_FORM)) {
      return [
        { title: "Third-Party Risk Assessment Settings", href: "", onClick: navigateBackToThirdParty },
        { title: "Intake Form", href: "" }
      ];
    }

    if (path.includes(SETTINGS_ROUTES.TIER_MAPPING)) {
      return [
        { title: "Third-Party Risk Agent Settings", href: "", onClick: navigateBackToThirdParty },
        { title: "Tier Mapping", href: "" }
      ];
    }

    if (path.includes(SETTINGS_ROUTES.CONTINUOUS_MONITORING)) {
      return [
        { title: "Third-Party Risk Agent Settings", href: "" },
        { title: "Continuous Monitoring", href: "" }
      ];
    }


    if (path.includes(SETTINGS_ROUTES.INTEGRATIONS_JIRA)) {
      return [
        { title: "Settings", href: `/${ROUTES.SETTINGS}` },
        { title: "Integrations", href: `/${ROUTES.SETTINGS}?tab=integrations` },
        { title: "Jira", href: "" }
      ];
    }

    if (path.includes(SETTINGS_ROUTES.INTEGRATIONS_GOOGLE_DRIVE)) {
      return [
        { title: "Settings", href: `/${ROUTES.SETTINGS}` },
        { title: "Integrations", href: `/${ROUTES.SETTINGS}?tab=integrations` },
        { title: "Google Drive", href: "" }
      ];
    }

    if (path.includes(SETTINGS_ROUTES.INTEGRATIONS)) {
      return [
        { title: "Settings", href: `/${ROUTES.SETTINGS}` },
        { title: "Integrations", href: `/${ROUTES.SETTINGS}?tab=integrations` } 
      ];
    }

    if (path.includes(SETTINGS_ROUTES.WHITELISTED_DOMAINS)) {
      return [
        { title: "Settings", href: "", onClick: navigateBackToGeneral },
        { title: "Whitelisted Domains", href: "" },

      ];
    }

    return [{ title: "Settings", href: "/settings" }];
  };

  // Determine if we should show Create New button
  const shouldShowCreateButton = () => {
    const path = location.pathname;

    return path.includes(SETTINGS_ROUTES.THIRD_PARTY_QUESTIONNAIRES) ||
      path.includes(SETTINGS_ROUTES.THIRD_PARTY_CONTROL_SETS) ||
      path.includes(SETTINGS_ROUTES.WHITELISTED_DOMAINS);
  };

  // Handle Create New button click
  const handleCreateNew = () => {
    if (location.pathname.includes(SETTINGS_ROUTES.THIRD_PARTY_QUESTIONNAIRES)) {
      window.dispatchEvent(new CustomEvent('openCreateQuestionnaireModal'));
    } else if (location.pathname.includes(SETTINGS_ROUTES.THIRD_PARTY_CONTROL_SETS)) {
      window.dispatchEvent(new CustomEvent('openCreateControlSetModal'));
    } else if (location.pathname.includes(SETTINGS_ROUTES.WHITELISTED_DOMAINS)) {
      window.dispatchEvent(new CustomEvent("openAddWhitelistDomainModal"));
    }
  };

  const getCreateButtonLabel = () => {
    if (location.pathname.includes(SETTINGS_ROUTES.WHITELISTED_DOMAINS)) {
      return "Add New";
    }

    return "Create New";
  };

  // Check if we're on the intake form route or continuous monitoring route
  const isIntakeFormRoute = location.pathname.includes(SETTINGS_ROUTES.INTAKE_FORM);
  const isContinuousMonitoringRoute = location.pathname.includes(SETTINGS_ROUTES.CONTINUOUS_MONITORING);

  // Handle Done button click
  const handleDoneClick = () => {
    if (isContinuousMonitoringRoute) {
      // Dispatch custom event for continuous monitoring to handle its own modal logic
      window.dispatchEvent(new CustomEvent('continuousMonitoringDone'));
    } else if (isIntakeFormRoute && handleSave) {
      // For intake form, use the existing save logic
      void handleSave();
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
        <div className="flex gap-4 items-center py-1.5">
          <ZBreadCrumbs items={getBreadcrumbItems()} />
        </div>
        {/* Show Done button for intake form route and continuous monitoring route */}
        {(isIntakeFormRoute || isContinuousMonitoringRoute) && (
          <div className="flex items-center gap-4">
            <ZButton
              color="primary"
              onClick={handleDoneClick}
              isDisabled={(isIntakeFormRoute || isContinuousMonitoringRoute) ? (isDisabled || isSaving) : false}
              isLoading={isSaving}
              className="px-3 py-1 h-auto mt-0.5"
            >
              Done
            </ZButton>
          </div>
        )}
        {/* Show Create New button for other routes */}
        {shouldShowCreateButton() && isAdmin && (
          <div className="flex gap-2">
            <ZButton
              color="primary"
              className="text-xs font-medium h-7 px-3"
              onClick={handleCreateNew}
            >
              {getCreateButtonLabel()}
            </ZButton>
          </div>
        )}
      </div>
      <div className="grow bg-white overflow-hidden rounded-lg">
        <Outlet />
      </div>
    </div>
  );
};

export default SettingsLayout;
